import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Button, Modal, notification, Space, Table, Tooltip } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getExaminerState } from "../../redux/store/getState";
import studentId from "../../utils/studentId";
import { ExamSession } from "../../redux/apiSlice/examinerSlice";
import { getExaminerScheduleExamInstances } from "../../redux/apiActions/examinerActions";
import useSearch from "../../hooks/useSearch";
import axiosClient from "../../api/axiosClient";
import handleApiError from "../../utils/handleError";
import useUser from "../../hooks/useUser";
import useLabReadiness from "./useLabReadiness";
import LabStatusTag from "./LabStatusTag";
import {
  LAB_STATE_LABEL,
  mergeLabReadiness,
  storedLabReadiness,
} from "./labReadiness";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

/* Slower than the per-row lab poll: this is one request for the whole table, and
   the states it carries (a candidate entering, the five-minute provisioning cron)
   move on that sort of scale anyway. */
const LIST_REFRESH_INTERVAL_MS = 30_000;

const ExaminerTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { examinerInstances, loading } = useAppSelector(getExaminerState);
  const [data, setData] = useState<ExamSession[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  useSearch(examinerInstances, setData);

  const { examinerEditPermission } = useUser();

  /* Lab readiness for the rows on screen. Verifying a candidate whose lab has not
     finished provisioning is refused by the backend, so the examiner needs to see
     that state rather than discover it by clicking Verify. */
  const { readiness, checking, liveUnavailable, refresh, applyReadiness } =
    useLabReadiness(data);

  /* The list payload carries a lab status of its own, so the column is never blank
     while a live check is in flight — and stays right on a backend that has no
     readiness endpoint at all, where the list is the only signal there is. */
  const labReadinessFor = (record: ExamSession) =>
    mergeLabReadiness(storedLabReadiness(record), readiness[record.id]);

  const [recheckingId, setRecheckingId] = useState<string | null>(null);

  /* The re-check button, which must answer the examiner's click on any backend.
     Where the readiness endpoint exists it asks that; where it does not, asking
     would only produce a 404 the examiner can do nothing with, so it re-reads the
     session list instead — the same signal the automatic refresh runs on, and the
     only one such a backend has. */
  async function recheckLab(record: ExamSession) {
    setRecheckingId(record.id);
    try {
      if (!liveUnavailable && (await refresh(record.id)) !== "unavailable") {
        return;
      }
      await dispatch(getExaminerScheduleExamInstances({ background: true }));
    } finally {
      setRecheckingId(null);
    }
  }

  useEffect(() => {
    dispatch(getExaminerScheduleExamInstances());
  }, [dispatch]);

  /* Re-poll the list itself, not just the labs. A candidate who starts their exam,
     a lab the provisioning cron promotes, a slot booked while this screen is open —
     none of it reached the examiner until they reloaded the browser, which is the
     one thing an examiner watching for a candidate should not have to remember to
     do. Flagged `background` so the table refreshes in place, without a spinner. */
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      dispatch(getExaminerScheduleExamInstances({ background: true }));
    }, LIST_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [dispatch]);

  async function cancelExamInstance(id: string) {
    try {
      await axiosClient.delete(`examinstances/${id}/cancel`);
      notification.success({
        message: "Exam cancelled successfully",
      });
      dispatch(getExaminerScheduleExamInstances());
    } catch (error: any) {
      handleApiError(error);
    }
  }

  async function verifyExamInstance(id: string) {
    try {
      await axiosClient.post(`examinstances/${id}/verify`);
      notification.success({
        message: "Student is verified now",
      });
      dispatch(getExaminerScheduleExamInstances());
    } catch (error: any) {
      /* A refusal on lab grounds carries the live readiness with it, so fold that
         into the Lab column: the next thing the examiner looks at should already
         say why, without another round trip. */
      if (error?.response?.data?.lab) {
        applyReadiness(id, error.response.data.lab);
      }
      handleApiError(error);
    }
  }

  // Both actions are irreversible from this screen and the button swaps from
  // Verify to Cancel in place once a student is verified — so a second click on
  // the same spot would otherwise end a running exam. Always confirm first.
  function confirmExamInstanceAction(record: ExamSession) {
    const isCancel = record?.status === "in_progress";
    const student = `${studentId(record.user)} (${record.user.username})`;
    const lab = labReadinessFor(record);

    Modal.confirm({
      centered: true,
      icon: null,
      title: (
        <span style={{ color: "#ffffff" }}>
          {isCancel ? "Cancel Exam?" : "Verify Student?"}
        </span>
      ),
      content: (
        <span style={{ color: "#ffffff" }}>
          {isCancel ? (
            <>
              This will end the exam already in progress for {student} on{" "}
              {record.exam.name}. The student cannot continue and this cannot be
              undone. Are you sure you want to cancel this exam?
            </>
          ) : (
            <>
              Confirm that you have completed proctor verification for {student}{" "}
              on {record.exam.name}. The student will be allowed to start the
              exam.
              {/* Verification starts the clock, so the state of the lab the student
                  is about to need belongs on this screen, not in a failed attempt. */}
              {lab.lab_required ? (
                <div style={{ marginTop: 12, opacity: 0.85 }}>
                  Lab: {LAB_STATE_LABEL[lab.state]} — {lab.message}
                </div>
              ) : null}
            </>
          )}
        </span>
      ),
      okText: isCancel ? "Yes, Cancel Exam" : "Yes, Verify",
      cancelText: isCancel ? "No, Keep Exam Running" : "Cancel",
      okButtonProps: { danger: isCancel },
      cancelButtonProps: {
        ghost: true,
        style: { borderColor: "#EC171E", color: "#EC171E" },
      },
      styles: {
        content: {
          background: "linear-gradient(180deg, #2d2d2d 0%, #232323 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 18,
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
        },
      },
      onOk: () =>
        isCancel
          ? cancelExamInstance(record.id)
          : verifyExamInstance(record.id),
    });
  }

  const columns: ColumnsType<ExamSession> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Student ID",
      dataIndex: "user",
      render: (user) => <>{studentId(user)}</>,
    },

    {
      title: "Username",
      dataIndex: "user",
      render: (user) => <>{user.username}</>,
    },
    {
      title: "Exam Name",
      dataIndex: "exam",
      render: (exam) => <>{exam.name}</>,
    },
    {
      title: "Slot Booking Time",
      dataIndex: "time",
      render: (time) => <>{moment.unix(time).format("MM/DD/YYYY hh:mmA")}</>,
    },
    {
      title: "Lab",
      key: "lab",
      render: (_, record) => (
        <LabStatusTag
          readiness={labReadinessFor(record)}
          checking={Boolean(checking[record.id]) || recheckingId === record.id}
          liveUnavailable={liveUnavailable}
          onRefresh={() => recheckLab(record)}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => {
        const isCancel = record?.status === "in_progress";
        /* Verify is held back rather than left to fail: the backend refuses a
           session whose lab is still provisioning, and an enabled button is what
           made examiners click it repeatedly to find out. Cancel is never gated —
           ending a running exam has nothing to do with the lab. */
        const labBlocked = !isCancel && !labReadinessFor(record).ready;

        return (
          <Space>
            <Tooltip title={labBlocked ? labReadinessFor(record).message : ""}>
              {/* A disabled antd button swallows hover, so the tooltip needs a live
                element of its own to hang off. */}
              <span style={{ display: "inline-block" }}>
                <Button
                  style={{
                    background: "#565656",
                    borderRadius: 12,
                    boxShadow: "0px 4px 4px 0px #00000040",
                    fontWeight: 500,
                    border: "none",
                  }}
                  type="primary"
                  onClick={() => confirmExamInstanceAction(record)}
                  disabled={!examinerEditPermission || labBlocked}
                >
                  {isCancel ? "Cancel" : "Verify"}
                </Button>
              </span>
            </Tooltip>

            <Button
              style={{
                background: "#565656",
                borderRadius: 12,
                boxShadow: "0px 4px 4px 0px #00000040",
                fontWeight: 500,
                border: "none",
              }}
              type="primary"
              onClick={() => window.open(record.proctor_connect_url, "_blank")}
              disabled={!examinerEditPermission}
            >
              Connect
            </Button>
          </Space>
        );
      },
    },
  ];
  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      loading={loading.get}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default ExaminerTable;
